package nl.volleyplanner.web

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.extensions.name
import kotlin.time.TimeSource

data class ApiResult<T>(val data: T?, val error: Throwable?)

/**
 * Not an actual StateFlow (should think about renaming)
 *
 * Stores data from a loadFunction in a MutableStateFlow, which is only populated after an initial subscribe()
 */
class ApiDataStateFlow<T, Context>(
    val name: String,
    val minAccessLevel: AuthLevel,
    val contextFlow: StateFlow<Context>,
    val coroutineScope: CoroutineScope = CoroutineManager.RENDER_SCOPE,
    initialState: T? = null,
    private val loadFunction: suspend (Context) -> T
) {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.base.ApiDataStateFlow")
    }

    private val apiResultState = MutableStateFlow<ApiResult<out T>?>(null)
    private val dataState = MutableStateFlow(initialState)

    private val subscriptionCountFlow = apiResultState.subscriptionCount
        .combine(dataState.subscriptionCount) { a, b -> a + b }
        .stateIn(coroutineScope, SharingStarted.Eagerly, apiResultState.subscriptionCount.value + dataState.subscriptionCount.value)
    private var _apiJob: Job? = null

    /**
     * The current value of the StateFlow loading the loadFunction
     * This is only populated after an initial subscribe()
     */
    val value: T?
        get() = apiResultState.value?.data

    /**
     * The error that occured during loadFunction execution
     * Always reset to null at each refresh, until next error
     */
    val error: Throwable?
        get() = apiResultState.value?.error

    init {
        val apiDataStateFlow = this
        coroutineScope.launch {
            logger.info { "[${apiDataStateFlow.name}@${coroutineScope.name}] Starting to listen to subscription count" }
            subscriptionCountFlow
                .map { it > 0 }
                .distinctUntilChanged()
                .onEach { hasSubscribers ->
                    if (hasSubscribers && (value == null) && _apiJob == null) {
                        logger.info("[${apiDataStateFlow.name}@${coroutineScope.name}] Received first subscriber. Starting refresh")
                        refresh()
                    } else {
                        logger.info("[${apiDataStateFlow.name}@${coroutineScope.name}] Received first subscriber. But no need to refresh")
                    }
                }.collect()
        }
        coroutineScope.launch {
            contextFlow.collectLatest { refresh() }
        }
    }

    fun refresh() {
        val _this = this
        dataState.value = null
        apiResultState.value = null
        _apiJob?.cancel()
        _apiJob = null

        // Only trigger a refresh if there are subscribers. Otherwise new subscribers will trigger a refresh
        // Also only trigger a refresh if the user has sufficient access level, otherwise we'll likely run into HTTP 401
        val context = contextFlow.value
        val userAccessLevel = AuthContextService.authUser?.authLevel ?: AuthLevel.AnonymousPublic
        logger.debug { "[${_this.name}@${coroutineScope.name}] Refreshing data $userAccessLevel >= $minAccessLevel = ${userAccessLevel >= minAccessLevel}, subscriptionCount = ${subscriptionCountFlow.value}" }
        if (userAccessLevel >= minAccessLevel && (subscriptionCountFlow.value > 0)) {
            _apiJob = coroutineScope.launch {
                logger.debug { "[${_this.name}@${coroutineScope.name}] Has subscribers, triggering loadFunction" }
                val mark = TimeSource.Monotonic.markNow()
                val apiResult = try {
                    ApiResult(loadFunction.invoke(context), null)
                } catch (e: Exception) {
                    ApiResult(null, e)
                }
                apiResultState.value = apiResult
                dataState.value = apiResult.data
                val elapsedMs = mark.elapsedNow().inWholeMilliseconds
                logger.debug("[${_this.name}@${coroutineScope.name}] Refreshed data in ${elapsedMs}ms for ${subscriptionCountFlow.value} subscribers")
            }
        } else {
            logger.debug("[${_this.name}@${coroutineScope.name}] Not refreshing data. userAccessLevel = $userAccessLevel, minAccessLevel = $minAccessLevel, subscriptionCount = ${subscriptionCountFlow.value}")
        }
    }

    fun subscribeApiResult(): StateFlow<ApiResult<out T>?> {
        return apiResultState
    }

    /**
     * Returns a StateFlow that only emits the data, never the ApiResult that can also contain errors
     * TODO: Determine if we actually want this. As it enables consumers to never process errors and get stuck
     *  Otherwise, this should at least be a Flow instead of StateFlow. So we can map it from apiResult
     */
    fun subscribeDataOnly(): StateFlow<T?> {
        return dataState
    }

    @Deprecated("Use subscribeDataOnly or subscribeApiResult instead")
    fun subscribe() = subscribeDataOnly()
}
